// ImportExportService.js

import config from '../config';
import selectedEnvironment from '../env';

// Define the API URL based on the selected environment
const API_URL = `${config[selectedEnvironment].API_URL}`;


/**
 * Preview the Movein.dat export. Returns the records that would be written plus the
 * unique SortCode names so the caller can build a mapping (SortCodeName -> short code)
 * before calling ExportTransactionReportMovinDat.
 */
const PreviewExportTransactionReportMovinDat = async (caseId, startDate, endDate, auth) => {
    try {
        const payload = {
            CaseId: caseId,
            StartDate: startDate,
            EndDate: endDate
        };

        const response = await fetch(`${API_URL}/ImportExportTransactions/PreviewExportTransactionReportMovinDat`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (err) {
        console.error('Error previewing MOVEIN.DAT export:', err);
        throw err;
    }
};

/**
 * Export Movein.dat (ZIP with movin.doc + movin.prm).
 * @param {string} caseId
 * @param {string|Date} startDate
 * @param {string|Date} endDate
 * @param {string} auth Bearer token
 * @param {Array<{SortCodeName:string, MappedCode:string}>} [sortCodeMappings] Optional. Each unique SortCode
 *   name (from preview) is mapped to a short MovementCode (max 3 chars, will be truncated by the server).
 */
const ExportTransactionReportMovinDat = async (caseId, startDate, endDate, auth, sortCodeMappings) => {
    try {
        const payload = {
            CaseId: caseId,
            StartDate: startDate,
            EndDate: endDate,
            SortCodeMappings: Array.isArray(sortCodeMappings) ? sortCodeMappings : null
        };

        const response = await fetch(`${API_URL}/ImportExportTransactions/ExportTransactionReportMovinDat`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        // Extract the Content-Disposition header to determine the file name
        const contentDisposition = response.headers.get('Content-Disposition');
        
        // Get the file content from the response
        const fileContent = await response.blob();
        
        // Create a URL for the file
        const fileUrl = window.URL.createObjectURL(fileContent);
        
        // Create a temporary link and trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        
        // Extract filename from Content-Disposition or use default
        let filename = 'MOVEIN.dat'; // Default filename
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
            }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        
        // Clean up the object URL
        window.URL.revokeObjectURL(fileUrl);
    } catch (err) {
        console.error('Error exporting MOVEIN.DAT:', err);
        throw err;
    }
};

const PreviewExportTransactionReportPkuda = async (caseId, startDate, endDate, auth, exportOptions = {}) => {
    try {
        const {
            includeIncomes = true,
            includeExpenses = true,
            includeJournalEntries = true
        } = exportOptions || {};

        const payload = {
            CaseId: caseId,
            StartDate: startDate,
            EndDate: endDate,
            IncludeIncomes: includeIncomes,
            IncludeExpenses: includeExpenses,
            IncludeJournalEntries: includeJournalEntries
        };

        const response = await fetch(`${API_URL}/ImportExportTransactions/PreviewExportTransactionReportPkuda`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (err) {
        console.error('Error previewing PKUDA export:', err);
        throw err;
    }
};

const ExportTransactionReportPkuda = async (caseId, startDate, endDate, auth, exportOptions = {}) => {
    try {
        const {
            includeIncomes = true,
            includeExpenses = true,
            includeJournalEntries = true
        } = exportOptions || {};

        // Prepare the payload from the parameters
        const payload = {
            CaseId: caseId,
            StartDate: startDate,
            EndDate: endDate,
            IncludeIncomes: includeIncomes,
            IncludeExpenses: includeExpenses,
            IncludeJournalEntries: includeJournalEntries
        };

        // Make the HTTP request to get the file data
        const response = await fetch(`${API_URL}/ImportExportTransactions/ExportTransactionReportPkuda`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        // Extract the Content-Disposition header to determine the file name
        const contentDisposition = response.headers.get('Content-Disposition');
        
        // Extract filename from Content-Disposition or use default
        let filename = 'PKUDA_Export.txt'; // Default filename
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
            }
        }

        // Get the file content from the response
        const fileContent = await response.blob();

        // Create a URL for the file
        const fileUrl = window.URL.createObjectURL(fileContent);

        // Create a temporary link and trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        // Clean up the object URL
        window.URL.revokeObjectURL(fileUrl);

    } catch (err) {
        console.error('Error exporting PKUDA:', err);
        throw err;
    }
};

const PreviewExportAccountCards = async (caseId, auth) => {
    try {
        const response = await fetch(`${API_URL}/ImportExportTransactions/PreviewExportAccountCards?caseId=${encodeURIComponent(caseId)}`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (err) {
        console.error('Error previewing account cards export:', err);
        throw err;
    }
};

const ExportAccountCardsPkuda = async (caseId, accountCards, auth) => {
    try {
        const payload = {
            CaseId: caseId,
            AccountCards: accountCards
        };

        const response = await fetch(`${API_URL}/ImportExportTransactions/ExportAccountCardsPkuda`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        // Extract the Content-Disposition header to determine the file name
        const contentDisposition = response.headers.get('Content-Disposition');
        
        // Extract filename from Content-Disposition or use default
        let filename = 'AccountCards_PKUDA_Export.txt';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
            }
        }

        // Get the file content from the response
        const fileContent = await response.blob();

        // Create a URL for the file
        const fileUrl = window.URL.createObjectURL(fileContent);

        // Create a temporary link and trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        // Clean up the object URL
        window.URL.revokeObjectURL(fileUrl);

    } catch (err) {
        console.error('Error exporting account cards PKUDA:', err);
        throw err;
    }
};


/** Line format: "Standard" (88-char fixed) or "Alternate" (space-separated layout). */
export const MovinDatLineFormat = { Standard: 'Standard', Alternate: 'Alternate' };

/**
 * Preview Movein.DAT import (what would be read and which journal entries would be created).
 * @param {string} caseId - The Case ID
 * @param {File} file - The Movin.DAT file
 * @param {string} auth - The Bearer token for authentication
 * @param {number} [amountDivisor=1] - 1 = no division, 10 = one decimal, 100 = two decimals
 * @param {string} [lineFormat='Standard'] - "Standard" (88-char fixed) or "Alternate" (space-separated)
 * @param {File|null} [prmFile=null] - Optional MOVEIN.PRM file for column layout (when provided, lineFormat is ignored and PRM defines parsing)
 * @param {boolean} [reverseDescription=false] - When true, reverse Hebrew description text (RTL stored in file)
 * @returns {Promise<{ entries: Array, missingSortCodes: Array, errorMessages: Array, totalLines: number, validCount: number, errorCount: number }>}
 */
const PreviewMovinDat = async (caseId, file, auth, amountDivisor = 1, lineFormat = 'Standard', prmFile = null, reverseDescription = false) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    if (amountDivisor === 1 || amountDivisor === 10 || amountDivisor === 100) {
        formData.append('amountDivisor', String(amountDivisor));
    }
    if (lineFormat === MovinDatLineFormat.Alternate || lineFormat === MovinDatLineFormat.Standard) {
        formData.append('lineFormat', lineFormat);
    }
    if (prmFile && prmFile instanceof File && prmFile.size > 0) {
        formData.append('prmFile', prmFile);
    }
    if (reverseDescription) {
        formData.append('reverseDescription', 'true');
    }
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-movin-dat`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview Movein.DAT');
    }
    return response.json();
};

/**
 * Import Transaction Report Movin.DAT
 * @param {string} caseId - The Case ID
 * @param {File} file - The Movin.DAT file
 * @param {string} auth - The Bearer token for authentication
 * @param {number} [amountDivisor=1] - 1 = no division (as-is, default), 10 = one decimal (e.g. 80000 → 8000.0), 100 = two decimals (e.g. 800000 → 8000.00)
 * @param {string} [lineFormat='Standard'] - "Standard" (88-char fixed) or "Alternate" (space-separated); ignored when prmFile is provided
 * @param {File|null} [prmFile=null] - Optional MOVEIN.PRM file for column layout
 * @param {boolean} [reverseDescription=false] - When true, reverse Hebrew description text (RTL stored in file)
 * @param {number|null} [journalEntryType=null] - Optional Enums.JournalEntryTypes value stamped on every imported entry
 * @returns {Promise<Object>} - Returns the response JSON
 */
const ImportTransactionReportMovinDat = async (caseId, file, auth, amountDivisor = 1, lineFormat = 'Standard', prmFile = null, reverseDescription = false, journalEntryType = null) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    if (prmFile && prmFile instanceof File && prmFile.size > 0) {
        formData.append('moveinPrm', prmFile);
    }

    const params = new URLSearchParams({ caseId });
    if (amountDivisor === 1 || amountDivisor === 10 || amountDivisor === 100) params.set('amountDivisor', String(amountDivisor));
    if (lineFormat === 'Alternate' || lineFormat === 'Standard') params.set('lineFormat', lineFormat);
    if (reverseDescription) params.set('reverseDescription', 'true');
    if (journalEntryType != null) params.set('journalEntryType', String(journalEntryType));
    const response = await fetch(`${API_URL}/ImportExportTransactions/ImportTransactionReportMovinDat?${params}`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${auth}`
            // Note: Do not set "Content-Type" when sending FormData
        },
        body: formData
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import transaction report Movin.DAT');
    }

    return await response.json();
};

/**
 * Preview Heshin account cards (PRM + DAT)
 * @param {string} caseId - The Case ID
 * @param {File} prmFile - The heshin.prm file
 * @param {File} datFile - The heshin.dat file
 * @param {string} auth - The Bearer token for authentication
 * @param {boolean} [reverseName=true] - If true, reverse the name read from the DAT (e.g. Hebrew RTL)
 * @returns {Promise<Object>} - { totalLines, validCount, errorCount, errorMessages, entries, problematicCards }
 */
const PreviewHeshinAccountCards = async (caseId, prmFile, datFile, auth, reverseName = true) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('prmFile', prmFile);
    formData.append('datFile', datFile);
    formData.append('reverseName', reverseName);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-heshin-account-cards`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) throw new Error(await response.text() || 'Heshin account cards preview failed');
    return response.json();
};

/**
 * Import Heshin account cards (PRM + DAT)
 * @param {string} caseId - The Case ID
 * @param {File} prmFile - The heshin.prm file
 * @param {File} datFile - The heshin.dat file
 * @param {string} auth - The Bearer token for authentication
 * @param {boolean} [reverseName=true] - If true, reverse the name read from the DAT
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages, problematicCards }
 */
const ImportHeshinAccountCards = async (caseId, prmFile, datFile, auth, reverseName = true) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('prmFile', prmFile);
    formData.append('datFile', datFile);
    formData.append('reverseName', reverseName);
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-heshin-account-cards`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) throw new Error(await response.text() || 'Heshin account cards import failed');
    return response.json();
};

/**
 * Import Account Cards Heshin.Dat (legacy endpoint)
 * @param {string} caseId - The Case ID
 * @param {File} prmFile - The heshin.prm file
 * @param {File} datFile - The heshin.dat file
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - Returns the response JSON with processing details
 */
const ImportAccountCardsHeshinDat = async (caseId, prmFile, datFile, auth) => {

    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('prmFile', prmFile);
    formData.append('datFile', datFile);

    const response = await fetch(`${API_URL}/ImportExportTransactions/ImportAccountCardsHeshinDat?caseId=${encodeURIComponent(caseId)}`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${auth}`
            // Do not set "Content-Type" for FormData
        },
        body: formData
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import Account Cards Heshin.Dat');
    }

    return await response.json();
};

/**
 * Export Account Cards Heshin.Dat
 * @param {string} caseId - The Case ID
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<{ blob: Blob, fileName: string }>} - Returns the ZIP file blob and name
 */
const ExportAccountCardsHeshinDat = async (caseId, auth) => {
    try {
        // Make the HTTP request to get the file data
        const response = await fetch(`${API_URL}/ImportExportTransactions/ExportAccountCardsHeshinDat?caseId=${encodeURIComponent(caseId)}`, {
            method: 'GET',
            headers: {
                "Authorization": `Bearer ${auth}`
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        // Extract the Content-Disposition header to determine the file name
        const contentDisposition = response.headers.get('Content-Disposition');
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        let filename = 'account_cards_heshin.zip'; // Default filename

        if (matches && matches[1]) {
            filename = matches[1].replace(/['"]/g, ''); // Remove quotes if any
        }

        // Get the file content from the response
        const fileContent = await response.blob();

        // Create a URL for the file
        const fileUrl = window.URL.createObjectURL(fileContent);

        // Create a temporary link and trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        // Clean up the object URL
        window.URL.revokeObjectURL(fileUrl);
    } catch (err) {
        console.error(err);
        alert("Error downloading the file. Please try again.");
    }
};


/**
 * Import Problematic Account Cards
 * @param {string} caseId - The Case ID
 * @param {Array<Object>} accountCardActions - List of account card actions
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<void>} - Returns nothing on success
 */
const ImportProblematicAccountCards = async (caseId, accountCardActions, auth) => {
    const response = await fetch(`${API_URL}/ImportExportTransactions/ImportProblomaticAccountCards?caseId=${encodeURIComponent(caseId)}`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${auth}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(accountCardActions)
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import problematic account cards');
    }

    return;
};


/**
 * Import Transaction Report Movin.DAT
 * @param {string} caseId - The Case ID
 * @param {File} file - The Movin.DAT file
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - Returns the response JSON
 */
const PreviewTransactionReportPkuda = async (caseId, accountantId, files, sortCodeWorkbook, auth) => {
    const formData = new FormData();
    formData.append('CaseId', caseId);
    formData.append('AccountantId', accountantId);

    (files ?? []).forEach(file => {
        formData.append('PkudaFiles', file);
    });

    if (sortCodeWorkbook) {
        formData.append('SortCodeWorkbook', sortCodeWorkbook);
    }

    const response = await fetch(`${API_URL}/ImportExportTransactions/ImportTransactionReportPkuda/Preview`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview PKUDA transactions');
    }

    return await response.json();
};

const CommitTransactionReportPkuda = async (caseId, accountantId, files, sortCodeWorkbook, skipDuplicates, auth) => {
    const formData = new FormData();
    formData.append('CaseId', caseId);
    formData.append('AccountantId', accountantId);

    (files ?? []).forEach(file => {
        formData.append('PkudaFiles', file);
    });

    if (sortCodeWorkbook) {
        formData.append('SortCodeWorkbook', sortCodeWorkbook);
    }

    formData.append('SkipDuplicates', String(skipDuplicates ?? true));

    const response = await fetch(`${API_URL}/ImportExportTransactions/ImportTransactionReportPkuda/Commit`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import PKUDA transactions');
    }

    return await response.json();
};

// -----------------------------------------------------------------------------
// ImportExportService – Atzmait CSV helpers
// -----------------------------------------------------------------------------
/**
 * Phase-1: Preview – upload the CSV, return rows + list of *missing* sort-codes
 *            (NO data is written to the DB on the server side)
 *
 * @param {string}  caseId
 * @param {string}  accountantId
 * @param {File}    file              – the *.csv* file selected by the user
 * @param {string}  auth              – bearer token
 * @returns {Promise<Object>}         – { processedCount, missingSortCodes:[{sortCodeId, sortCodeName, sortCodeType, sortCodeCategory}], … }
 */
const PreviewTransactionReportAtzmaitCsv = async (caseId, accountantId, file, auth) => {
    const form = new FormData();
    form.append('file', file);
    form.append('caseId', caseId);
    form.append('accountantId', accountantId);

    const res = await fetch(`${API_URL}/ImportExportTransactions/ImportAtzmait/Preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });

    if (!res.ok) throw new Error(await res.text());
    return await res.json();
};

/**
 * Phase-2: Commit – *after* all missing sort-codes exist, upload the same file
 *           again and let the server persist the transactions.
 *
 * @param {string} caseId
 * @param {string} accountantId
 * @param {File}   file
 * @param {string} auth
 * @returns {Promise<Object>} – { processedCount, errorCount, errorMessages }
 */
const CommitTransactionReportAtzmaitCsv = async (caseId, accountantId, file, auth, importDuplicates = false) => {
    const form = new FormData();
    form.append('file', file);
    form.append('caseId', caseId);
    form.append('accountantId', accountantId);
    form.append('importDuplicates', importDuplicates);

    const res = await fetch(`${API_URL}/ImportExportTransactions/ImportAtzmait/Commit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });

    if (!res.ok) throw new Error(await res.text());
    return await res.json();
};

// -----------------------------------------------------------------------------
// ImportExportService – Atzmait backup (zip) helpers
// -----------------------------------------------------------------------------
const atzmaitBackupForm = ({ accountantId, file, password, year }) => {
    const form = new FormData();
    form.append('file', file);
    form.append('accountantId', accountantId);
    if (password) form.append('password', password);
    if (year) form.append('year', year);
    return form;
};

/**
 * Reads an עצמאית backup zip. With caseId: that client's entries; without: every business of the backup and the
 * client it matches. Errors are thrown with the server's key (e.g. ATZMAIT_WRONG_PASSWORD) as the message.
 * @param {{businessNumber: number, caseId: string}[]} [assignments] – clients chosen by hand; an empty caseId leaves the business without a client
 * @param {{sortCodeName: string, sortCodeGroup?: string, sortCodeVatType?: number, vat?: number, participatesInProfitAndLoss?: boolean}[]} [sortCodes]
 *   – the settings of the missing sort codes, which the profit and loss after the import is simulated with
 * @param {boolean} [importDuplicates] – simulate the import with the movements already in the system too
 * @param {number[]} [checkBusinessNumbers] – all clients: check only these businesses against the system
 * @returns {Promise<Object>} – { year, years, businesses, missingSortCodes, clients, selectedBusinessNumber, entries, duplicateCount, parseErrors }
 */
const PreviewAtzmaitBackup = async ({
    accountantId, file, password, year, caseId, businessNumber, assignments, sortCodes, importDuplicates, checkBusinessNumbers,
}, auth) => {
    const form = atzmaitBackupForm({ accountantId, file, password, year });
    if (caseId) form.append('caseId', caseId);
    if (businessNumber != null) form.append('businessNumber', businessNumber);
    if (assignments?.length) form.append('assignments', JSON.stringify(assignments));
    if (sortCodes?.length) form.append('sortCodes', JSON.stringify(sortCodes));
    if (importDuplicates) form.append('importDuplicates', 'true');
    if (checkBusinessNumbers) form.append('checkBusinessNumbers', JSON.stringify(checkBusinessNumbers));

    const res = await fetch(`${API_URL}/ImportExportTransactions/ImportAtzmait/Backup/Preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });

    if (!res.ok) throw new Error(await res.text());
    return await res.json();
};

/**
 * Imports the backup's businesses into their clients.
 * @param {{businessNumber: number, caseId: string}[]} assignments
 * @returns {Promise<Object>} – { results: [{ businessNumber, caseId, processedCount, errorCount, errorMessages }], processedCount, errorCount }
 */
const CommitAtzmaitBackup = async ({ accountantId, file, password, year, assignments, importDuplicates }, auth) => {
    const form = atzmaitBackupForm({ accountantId, file, password, year });
    form.append('assignments', JSON.stringify(assignments));
    form.append('importDuplicates', importDuplicates ? 'true' : 'false');

    const res = await fetch(`${API_URL}/ImportExportTransactions/ImportAtzmait/Backup/Commit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });

    if (!res.ok) throw new Error(await res.text());
    return await res.json();
};


const UploadAccountCardsFileDataRevhit = async (caseId, file, auth) => {
    const formData = new FormData();
    formData.append("CaseId", caseId);
    formData.append("File", file);

    const response = await fetch(`${API_URL}/ImportExportTransactions/UploadFromRevhitCSV`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${auth}`,
        },
        body: formData
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import Account Cards Heshin.Dat');
    }

    return await response.json();
};

/**
 * Phase‑1 – upload the MDB & let the server create **account‑cards**.  
 *           Returns duplicates in `problematicCards`.
 *
 *     POST  /ImportExportTransactions/import‑mdb‑cards
 *
 * @returns { processedCount, errorCount, errorMessages, problematicCards }
 */
const ImportMdbAccountCards = async (caseId, mdbFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', mdbFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-mdb-cards`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form            // do NOT set Content‑Type
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'MDB card import failed');
    return await res.json();
};

/**
 * Phase‑2 – *after* duplicates (if any) were handled, upload the SAME MDB
 *           again so the server inserts the **journal‑entries**.
 *
 *     POST  /ImportExportTransactions/import‑mdb‑journal
 *
 * @returns { processedCount, errorCount, errorMessages }
 */
/**
 * Preview MDB journal entries with sortcode validation
 */
const PreviewMdbJournalEntries = async (caseId, accountantId, mdbFile, sortCodeFile, auth, options = {}) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', mdbFile);

    if (sortCodeFile) {
        form.append('SortCodeWorkbook', sortCodeFile);
    }

    if (options.reportingMonthSource) {
        form.append('ReportingMonthSource', options.reportingMonthSource);
    }

    if (options.registrationMonthSource) {
        form.append('RegistrationMonthSource', options.registrationMonthSource);
    }

    if (options.vatReportFiles?.length) {
        options.vatReportFiles.forEach((item) => {
            const file = item?.file ?? item;
            const yearMonth = item?.yearMonth;
            if (file) {
                form.append('VatReportFiles', file);
                form.append('VatReportYearMonths', yearMonth || '');
            }
        });
    }

    if (options.cardClassification) {
        form.append('CardClassificationJson', JSON.stringify(options.cardClassification));
    }

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-mdb-journal`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'MDB journal preview failed');
    return await res.json();
};

/**
 * Import MDB journal entries (after sortcodes are created)
 */
const ImportMdbJournalEntries = async (caseId, mdbFile, sortCodeFile, defaultSortCodeNameForEmpty, skipDuplicates, auth, options = {}) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', mdbFile);

    if (sortCodeFile) {
        form.append('SortCodeWorkbook', sortCodeFile);
    }

    if (defaultSortCodeNameForEmpty) {
        form.append('DefaultSortCodeNameForEmptyTransType', defaultSortCodeNameForEmpty);
    }

    form.append('SkipDuplicates', String(skipDuplicates ?? true));

    if (options.reportingMonthSource) {
        form.append('ReportingMonthSource', options.reportingMonthSource);
    }
    if (options.registrationMonthSource) {
        form.append('RegistrationMonthSource', options.registrationMonthSource);
    }
    if (options.documentDateFrom) {
        form.append('DocumentDateFrom', options.documentDateFrom);
    }
    if (options.documentDateTo) {
        form.append('DocumentDateTo', options.documentDateTo);
    }
    if (options.reportingMonthFrom) {
        form.append('ReportingMonthFrom', options.reportingMonthFrom);
    }
    if (options.reportingMonthTo) {
        form.append('ReportingMonthTo', options.reportingMonthTo);
    }
    if (options.vatReportFiles?.length) {
        options.vatReportFiles.forEach((item) => {
            const file = item?.file ?? item;
            const yearMonth = item?.yearMonth;
            if (file) {
                form.append('VatReportFiles', file);
                form.append('VatReportYearMonths', yearMonth || '');
            }
        });
    }

    if (options.cardClassification) {
        form.append('CardClassificationJson', JSON.stringify(options.cardClassification));
    }

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-mdb-journal`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'MDB journal import failed');
    return await res.json();
};

/**
 * Preview Excel journal entries with sortcode validation
 */
const PreviewExcelJournalEntries = async (caseId, accountantId, excelFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', excelFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-excel-journal`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Excel journal preview failed');
    return await res.json();
};

/**
 * Import Excel journal entries (after sortcodes created)
 */
const ImportExcelJournalEntries = async (caseId, excelFile, defaultSortCodeNameForEmpty, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', excelFile);
    
    if (defaultSortCodeNameForEmpty) {
        form.append('DefaultSortCodeNameForEmptyTransType', defaultSortCodeNameForEmpty);
    }

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-excel-journal`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Excel journal import failed');
    return await res.json();
};

/**
 * Preview Excel account cards
 */
const PreviewExcelAccountCards = async (caseId, accountantId, excelFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', excelFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-excel-account-cards`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Excel account cards preview failed');
    return await res.json();
};

/**
 * Import Excel account cards
 */
const ImportExcelAccountCards = async (caseId, excelFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', excelFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-excel-account-cards`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Excel account cards import failed');
    return await res.json();
};

// -----------------------------------------------------------------------------
// Sumit Import Functions
// -----------------------------------------------------------------------------

/**
 * Preview Sumit account cards from Excel file
 * @param {string} caseId - The Case ID
 * @param {string} accountantId - The Accountant ID
 * @param {File} excelFile - The Excel file with account cards
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - { parsedRows, accountCards, missingPalCodes }
 */
const PreviewSumitAccountCards = async (caseId, accountantId, excelFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', excelFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-sumit-account-cards`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Sumit account cards preview failed');
    return await res.json();
};

/**
 * Import Sumit account cards from Excel file
 * @param {string} caseId - The Case ID
 * @param {File} excelFile - The Excel file with account cards
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages, problematicCards }
 */
const ImportSumitAccountCards = async (caseId, excelFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', excelFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-sumit-account-cards`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Sumit account cards import failed');
    return await res.json();
};

/**
 * Preview Sumit journal entries from Excel file(s)
 * Structure type is auto-detected by backend based on document URL column
 * @param {string} caseId - The Case ID
 * @param {string} accountantId - The Accountant ID
 * @param {File} journalFile - The Excel file with journal entries
 * @param {File|null} transactionTypesFile - Optional Excel file with transaction types (sortcodes)
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - { parsedRows, detectedStructureType, journalEntries, missingSortCodes, missingAccountCards, missingSortCodeCategories }
 */
const PreviewSumitJournal = async (caseId, accountantId, journalFile, transactionTypesFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('JournalFile', journalFile);
    
    if (transactionTypesFile) {
        form.append('TransactionTypesFile', transactionTypesFile);
    }

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-sumit-journal`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Sumit journal preview failed');
    return await res.json();
};

/**
 * Import Sumit journal entries from Excel file(s)
 * Structure type is auto-detected by backend
 * Backend also auto-creates missing sortcode categories
 * @param {string} caseId - The Case ID
 * @param {File} journalFile - The Excel file with journal entries
 * @param {File|null} transactionTypesFile - Optional Excel file with transaction types
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages }
 */
const ImportSumitJournal = async (caseId, journalFile, transactionTypesFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('JournalFile', journalFile);
    
    if (transactionTypesFile) {
        form.append('TransactionTypesFile', transactionTypesFile);
    }

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-sumit-journal`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Sumit journal import failed');
    return await res.json();
};

// -----------------------------------------------------------------------------
// Finbot Import Functions
// -----------------------------------------------------------------------------

/**
 * Preview Finbot account cards from Excel file
 * @param {string} caseId - The Case ID
 * @param {string} accountantId - The Accountant ID
 * @param {File} excelFile - The Excel file with account cards
 * @param {string} auth - The Bearer token for authentication
 * @param {number} [templateVersion=1] - 1 = classic Finbot cards; 2 = customers list (name-match)
 * @returns {Promise<Object>} - { parsedRows, accountCards, missingPalCodes }
 */
const PreviewFinbotAccountCards = async (caseId, accountantId, excelFile, auth, templateVersion = 1) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', excelFile);
    form.append('TemplateVersion', String(templateVersion || 1));

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-finbot-account-cards`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot account cards preview failed');
    return await res.json();
};

/**
 * Import Finbot account cards from Excel file
 * @param {string} caseId - The Case ID
 * @param {File} excelFile - The Excel file with account cards
 * @param {string} auth - The Bearer token for authentication
 * @param {number} [templateVersion=1] - 1 = classic Finbot cards; 2 = customers list (name-match)
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages, problematicCards }
 */
const ImportFinbotAccountCards = async (caseId, excelFile, auth, templateVersion = 1) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', excelFile);
    form.append('TemplateVersion', String(templateVersion || 1));

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-finbot-account-cards`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot account cards import failed');
    return await res.json();
};

/**
 * Preview Finbot journal entries from a single Excel file
 * @param {string} caseId - The Case ID
 * @param {string} accountantId - The Accountant ID
 * @param {File} journalFile - The Excel file with journal entries
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - { parsedRows, journalEntries, missingSortCodes, missingAccountCards }
 */
const PreviewFinbotJournal = async (caseId, accountantId, journalFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('JournalFile', journalFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-finbot-journal`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot journal preview failed');
    return await res.json();
};

/**
 * Import Finbot journal entries from a single Excel file
 * @param {string} caseId - The Case ID
 * @param {File} journalFile - The Excel file with journal entries
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages }
 */
const ImportFinbotJournal = async (caseId, journalFile, auth, isTempJournal = false) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('JournalFile', journalFile);
    form.append('IsTempJournal', String(isTempJournal === true));

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-finbot-journal`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot journal import failed');
    return await res.json();
};

// -----------------------------------------------------------------------------
// Finbot API Import Functions (fetch directly from the Finbot API instead of a file)
// -----------------------------------------------------------------------------

const buildFinbotApiPayload = ({
    caseId,
    accountantId,
    finbotToken,
    finbotUserAccountId,
    subdomain,
    fromDate,
    toDate,
    isTempJournal,
    downloadDocuments,
    dateType,
    tempDateType,
}) => {
    const payload = {
        CaseId: caseId,
        FinbotToken: finbotToken,
        FinbotUserAccountId: String(finbotUserAccountId ?? ''),
        Subdomain: subdomain,
    };
    if (accountantId != null) payload.AccountantId = accountantId;
    if (fromDate != null) payload.FromDate = fromDate;
    if (toDate != null) payload.ToDate = toDate;
    if (isTempJournal != null) payload.IsTempJournal = isTempJournal === true;
    if (downloadDocuments != null) payload.DownloadDocuments = downloadDocuments === true;
    if (dateType) payload.DateType = dateType;
    if (tempDateType) payload.TempDateType = tempDateType;
    return payload;
};

/**
 * List the Finbot clients/accounts under the accountant (for the import client picker).
 * @param {Object} params - { finbotToken, finbotUserAccountId, subdomain }
 * @param {string} auth - The app Bearer token
 * @returns {Promise<Array>} - [{ id, name, companyId }]
 */
const GetFinbotClients = async ({ finbotToken, finbotUserAccountId, subdomain }, auth) => {
    const res = await fetch(
        `${API_URL}/ImportExportTransactions/finbot-clients`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
                FinbotToken: finbotToken,
                FinbotUserAccountId: String(finbotUserAccountId ?? ''),
                Subdomain: subdomain,
            }),
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot clients listing failed');
    return await res.json();
};

/**
 * Preview Finbot journal entries fetched directly from the Finbot API.
 * @param {Object} params - { caseId, accountantId, finbotToken, finbotUserAccountId, subdomain, fromDate, toDate, isTempJournal }
 * @param {string} auth - The app Bearer token
 * @returns {Promise<Object>} - { parsedRows, journalEntries, missingSortCodes, missingAccountCards }
 */
const PreviewFinbotJournalApi = async (params, auth) => {
    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-finbot-journal-api`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(buildFinbotApiPayload(params)),
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot journal API preview failed');
    return await res.json();
};

/**
 * Import Finbot journal entries fetched directly from the Finbot API.
 * @param {Object} params - { caseId, finbotToken, finbotUserAccountId, subdomain, fromDate, toDate, isTempJournal }
 * @param {string} auth - The app Bearer token
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages }
 */
const ImportFinbotJournalApi = async (params, auth) => {
    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-finbot-journal-api`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(buildFinbotApiPayload(params)),
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot journal API import failed');
    return await res.json();
};

/**
 * Preview Finbot account cards fetched directly from the Finbot API (cards/search).
 * @param {Object} params - { caseId, accountantId, finbotToken, finbotUserAccountId, subdomain }
 * @param {string} auth - The app Bearer token
 * @returns {Promise<Object>} - { parsedRows, accountCards, missingPalCodes }
 */
const PreviewFinbotAccountCardsApi = async (params, auth) => {
    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-finbot-account-cards-api`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(buildFinbotApiPayload(params)),
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot account cards API preview failed');
    return await res.json();
};

/**
 * Import Finbot account cards fetched directly from the Finbot API (cards/search).
 * @param {Object} params - { caseId, finbotToken, finbotUserAccountId, subdomain }
 * @param {string} auth - The app Bearer token
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages, problematicCards }
 */
const ImportFinbotAccountCardsApi = async (params, auth) => {
    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-finbot-account-cards-api`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(buildFinbotApiPayload(params)),
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot account cards API import failed');
    return await res.json();
};

// -----------------------------------------------------------------------------
// Paperless API Import Functions
//
// Paperless models a period as one flat document set carrying both the counterparty and the
// sort-code tag, so account cards and journal entries share a single preview and a single import.
// -----------------------------------------------------------------------------

const buildPaperlessApiPayload = ({
    caseId,
    accountantId,
    paperlessToken,
    paperlessUserId,
    periodStart,
    periodEnd,
    dateType,
    downloadDocuments,
}) => {
    const payload = {
        CaseId: caseId,
        PaperlessToken: paperlessToken,
        PaperlessUserId: String(paperlessUserId ?? ''),
    };
    if (accountantId != null) payload.AccountantId = accountantId;
    if (periodStart != null) payload.PeriodStart = periodStart;
    if (periodEnd != null) payload.PeriodEnd = periodEnd;
    if (dateType != null) payload.DateType = Number(dateType) || 0;
    if (downloadDocuments != null) payload.DownloadDocuments = downloadDocuments === true;
    return payload;
};

/**
 * List the Paperless businesses under the accountant (for the import business picker).
 * @param {Object} params - { paperlessToken }
 * @param {string} auth - The app Bearer token
 * @returns {Promise<Array>} - [{ id, name, companyId, documentCount, isDisabled }]
 */
const GetPaperlessUsers = async ({ paperlessToken }, auth) => {
    const res = await fetch(
        `${API_URL}/ImportExportTransactions/paperless-users`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ PaperlessToken: paperlessToken }),
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Paperless businesses listing failed');
    return await res.json();
};

/**
 * Preview the account cards and journal entries a Paperless business would produce.
 * @param {Object} params - { caseId, accountantId, paperlessToken, paperlessUserId, periodStart, periodEnd, dateType }
 * @param {string} auth - The app Bearer token
 * @returns {Promise<Object>} - { parsedRows, skippedRows, duplicateCount, newRows, accountCards, missingPalCodes, journalEntries, missingSortCodes }
 */
const PreviewPaperlessApi = async (params, auth) => {
    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-paperless-api`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(buildPaperlessApiPayload(params)),
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Paperless API preview failed');
    return await res.json();
};

/**
 * Import the account cards and journal entries for a Paperless business.
 * @param {Object} params - { caseId, accountantId, paperlessToken, paperlessUserId, periodStart, periodEnd, dateType, downloadDocuments }
 * @param {string} auth - The app Bearer token
 * @returns {Promise<Object>} - { processedCount, cardCount, errorCount, errorMessages }
 */
const ImportPaperlessApi = async (params, auth) => {
    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-paperless-api`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(buildPaperlessApiPayload(params)),
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Paperless API import failed');
    return await res.json();
};

// -----------------------------------------------------------------------------
// Ravginit (RAVGONIT .26) Import Functions
// -----------------------------------------------------------------------------

/**
 * Preview Ravginit account cards from RAVGONIT export file
 * @param {string} caseId - The Case ID
 * @param {string} accountantId - The Accountant ID
 * @param {File} file - The RAVGONIT export file (extension = year, e.g. .26)
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - { parsedRows, accountCards, missingPalCodes }
 */
const PreviewRavginitAccountCards = async (caseId, accountantId, file, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', file);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-ravginit-account-cards`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Ravginit account cards preview failed');
    return await res.json();
};

/**
 * Import Ravginit account cards from RAVGONIT export file
 * @param {string} caseId - The Case ID
 * @param {File} file - The RAVGONIT export file
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages, problematicCards }
 */
const ImportRavginitAccountCards = async (caseId, file, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', file);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-ravginit-account-cards`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Ravginit account cards import failed');
    return await res.json();
};

/**
 * Preview Ravginit journal entries from RAVGONIT export file
 * @param {string} caseId - The Case ID
 * @param {string} accountantId - The Accountant ID
 * @param {File} file - The RAVGONIT export file
 * @param {string} auth - The Bearer token for authentication
 * @returns {Promise<Object>} - { parsedJournalEntries, entries, missingSortCodes, missingAccountCards }
 */
const PreviewRavginitJournal = async (caseId, accountantId, file, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', file);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-ravginit-journal`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Ravginit journal preview failed');
    return await res.json();
};

/**
 * Import Ravginit journal entries from RAVGONIT export file
 * @param {string} caseId - The Case ID
 * @param {File} file - The RAVGONIT export file
 * @param {string} auth - The Bearer token for authentication
 * @param {boolean} skipDuplicates - Whether to skip duplicate entries (default true)
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages }
 */
const ImportRavginitJournal = async (caseId, file, auth, skipDuplicates = true) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', file);
    form.append('SkipDuplicates', String(skipDuplicates));

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-ravginit-journal`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Ravginit journal import failed');
    return await res.json();
};

// -----------------------------------------------------------------------------
// KodBena Import Functions
// -----------------------------------------------------------------------------

// Account Cards
const PreviewKodBenaAccountCards = async (caseId, accountantId, mdbFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', mdbFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-kodbena-account-cards`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'KodBena account cards preview failed');
    return await res.json();
};

const ImportKodBenaAccountCards = async (caseId, mdbFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', mdbFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-kodbena-account-cards`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'KodBena account cards import failed');
    return await res.json();
};

// Journal Entries
const PreviewKodBenaJournal = async (caseId, accountantId, mdbFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', mdbFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-kodbena-journal`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'KodBena journal preview failed');
    return await res.json();
};

const ImportKodBenaJournal = async (caseId, mdbFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', mdbFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-kodbena-journal`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'KodBena journal import failed');
    return await res.json();
};

// -----------------------------------------------------------------------------
// Taiodet Import Functions
// -----------------------------------------------------------------------------

// Account Cards
const PreviewTaiodetAccountCards = async (caseId, accountantId, csvFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', csvFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-taiodet-account-cards`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Taiodet account cards preview failed');
    return await res.json();
};

const ImportTaiodetAccountCards = async (caseId, csvFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', csvFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-taiodet-account-cards`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Taiodet account cards import failed');
    return await res.json();
};

// Journal Entries
const PreviewTaiodetJournal = async (caseId, accountantId, csvFile, date, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', csvFile);
    form.append('Date', date.toISOString());

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-taiodet-journal`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Taiodet journal preview failed');
    return await res.json();
};

const ImportTaiodetJournal = async (caseId, csvFile, date, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', csvFile);
    form.append('Date', date.toISOString());

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-taiodet-journal`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Taiodet journal import failed');
    return await res.json();
};

// ═══════════════════════════════════════════════════════════════════════════════
// PRIORITY IMPORT
// ═══════════════════════════════════════════════════════════════════════════════

// Account Cards
const PreviewPriorityAccountCards = async (caseId, accountantId, excelFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', excelFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-priority-account-cards`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Priority account cards preview failed');
    return await res.json();
};

const ImportPriorityAccountCards = async (caseId, excelFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', excelFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-priority-account-cards`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Priority account cards import failed');
    return await res.json();
};

// Journal Entries
const PreviewPriorityJournal = async (caseId, accountantId, journalFile, transactionTypesFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('JournalFile', journalFile);
    if (transactionTypesFile) {
        form.append('TransactionTypesFile', transactionTypesFile);
    }

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-priority-journal`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Priority journal preview failed');
    return await res.json();
};

const ImportPriorityJournal = async (caseId, journalFile, transactionTypesFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('JournalFile', journalFile);
    if (transactionTypesFile) {
        form.append('TransactionTypesFile', transactionTypesFile);
    }

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-priority-journal`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Priority journal import failed');
    return await res.json();
};

const PreviewPriorityOpeningBalance = async (caseId, accountantId, excelFile, month, year, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', excelFile);
    if (month != null) form.append('Month', month.toString());
    if (year != null) form.append('Year', year.toString());

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-priority-opening-balance`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Priority opening balance preview failed');
    return await res.json();
};

const ImportPriorityOpeningBalance = async (caseId, excelFile, month, year, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', excelFile);
    if (month != null) form.append('Month', month.toString());
    if (year != null) form.append('Year', year.toString());

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-priority-opening-balance`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'Priority opening balance import failed');
    return await res.json();
};

// Consolidate all functions into the service object
// -----------------------------------------------------------------------------
// Rivhit CSV V2 Import Functions (Dynamic Header Reading)
// -----------------------------------------------------------------------------

/**
 * Preview Rivhit CSV V2 with dynamic header reading
 * @param {string} caseId - The Case ID
 * @param {string} accountantId - The Accountant ID
 * @param {File} csvFile - The CSV file
 * @param {string} auth - The Bearer token
 * @returns {Promise<Object>} - { parsedRows, missingPalCodes }
 */
const PreviewRivhitCsvV2 = async (caseId, accountantId, csvFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', csvFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-rivhit-csv-v2`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Rivhit CSV V2 preview failed');
    return await res.json();
};

/**
 * Import Rivhit CSV V2
 * @param {string} caseId - The Case ID
 * @param {File} csvFile - The CSV file
 * @param {string} auth - The Bearer token
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages }
 */
const ImportRivhitCsvV2 = async (caseId, csvFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', csvFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-rivhit-csv-v2`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Rivhit CSV V2 import failed');
    return await res.json();
};

// -----------------------------------------------------------------------------
// Account Index Import Functions (RAVGONIT / NIHULIT chart-of-accounts Excel)
// -----------------------------------------------------------------------------

/**
 * Preview / import an "אינדקס חשבונות" Excel export as account cards.
 * @param {string} endpoint - The controller action name
 * @param {string} caseId - The Case ID
 * @param {string} accountantId - The Accountant ID
 * @param {File} file - The Excel file
 * @param {string} auth - The Bearer token
 * @returns {Promise<Object>} The preview or import response
 */
const callAccountIndexEndpoint = async (endpoint, caseId, accountantId, file, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', file);

    const res = await fetch(`${API_URL}/ImportExportTransactions/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });

    if (!res.ok) throw new Error(await res.text() || `${endpoint} failed`);
    return await res.json();
};

const PreviewRavginitAccountIndex = (caseId, accountantId, file, auth) =>
    callAccountIndexEndpoint('preview-ravginit-account-index', caseId, accountantId, file, auth);

const ImportRavginitAccountIndex = (caseId, accountantId, file, auth) =>
    callAccountIndexEndpoint('import-ravginit-account-index', caseId, accountantId, file, auth);

const PreviewNihulitAccountIndex = (caseId, accountantId, file, auth) =>
    callAccountIndexEndpoint('preview-nihulit-account-index', caseId, accountantId, file, auth);

const ImportNihulitAccountIndex = (caseId, accountantId, file, auth) =>
    callAccountIndexEndpoint('import-nihulit-account-index', caseId, accountantId, file, auth);

// -----------------------------------------------------------------------------
// BMKVD (Uniform File / קובץ אחיד) Export Functions
// -----------------------------------------------------------------------------

/**
 * Preview BMKVD export - shows record counts and document type summaries without generating files
 * @param {string} caseId - Client case ID
 * @param {Date} startDate - Start date for the report period
 * @param {Date} endDate - End date for the report period
 * @param {string} auth - Authorization token
 * @returns {Promise<Object>} Preview data including record counts and summaries
 */
const PreviewBmkvdExport = async (
    caseId,
    startDate,
    endDate,
    auth,
    receiptSortCodeIds = [],
    inventorySortCodeIds = [],
    filterByDocumentDate = false,
    onlyProcessed = true
) => {
    try {
        const payload = {
            CaseId: caseId,
            Date: startDate,
            EndDate: endDate,
            ReceiptSortCodeIds: receiptSortCodeIds || [],
            InventorySortCodeIds: inventorySortCodeIds || [],
            FilterByDocumentDate: !!filterByDocumentDate,
            OnlyProcessed: onlyProcessed !== false
        };

        const response = await fetch(`${API_URL}/Bmkvd/Preview`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorText = await response.text();
            let message = errorText || `HTTP error! status: ${response.status}`;
            try {
                const parsed = JSON.parse(errorText);
                if (parsed && typeof parsed === 'object') {
                    message = [parsed.message, parsed.error].filter(Boolean).join(': ') || message;
                }
            } catch {
                /* keep message as raw text */
            }
            throw new Error(message);
        }

        return await response.json();
    } catch (err) {
        console.error('Error previewing BMKVD export:', err);
        throw err;
    }
};

/**
 * Download complete BMKVD export package (ZIP with BMKVD.zip, INI.txt, and PDFs)
 * @param {string} caseId - Client case ID
 * @param {Date} startDate - Start date for the report period
 * @param {Date} endDate - End date for the report period
 * @param {string} auth - Authorization token
 */
const DownloadBmkvdExport = async (
    caseId,
    startDate,
    endDate,
    auth,
    receiptSortCodeIds = [],
    inventorySortCodeIds = [],
    filterByDocumentDate = false,
    onlyProcessed = true
) => {
    try {
        const payload = {
            CaseId: caseId,
            Date: startDate,
            EndDate: endDate,
            ReceiptSortCodeIds: receiptSortCodeIds || [],
            InventorySortCodeIds: inventorySortCodeIds || [],
            FilterByDocumentDate: !!filterByDocumentDate,
            OnlyProcessed: onlyProcessed !== false
        };

        const response = await fetch(`${API_URL}/Bmkvd/Download`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        // Extract the Content-Disposition header to determine the file name
        const contentDisposition = response.headers.get('Content-Disposition');
        
        // Get the file content from the response
        const fileContent = await response.blob();
        
        // Create a URL for the file
        const fileUrl = window.URL.createObjectURL(fileContent);
        
        // Create a temporary link and trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        
        // Extract filename from Content-Disposition or use default
        let filename = 'UniformFile.zip'; // Default filename
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
            }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        
        // Clean up the object URL
        window.URL.revokeObjectURL(fileUrl);
    } catch (err) {
        console.error('Error downloading BMKVD export:', err);
        throw err;
    }
};

/**
 * Preview journal entries rebuilt from a uniform file (BKMVDATA.txt or a ZIP containing it).
 * @param {string} caseId - Client case ID
 * @param {string} accountantId - The Accountant ID (sortcodes are per-accountant)
 * @param {File} file - BKMVDATA.txt or a ZIP containing it
 * @param {string} auth - Authorization token
 * @param {number} maxPreviewRows - Cap on preview rows (0 returns all)
 * @returns {Promise<Object>} - { entries, missingSortCodes, missingAccountCards, missingPalCodes, ... }
 */
const PreviewBmkvdJournal = async (caseId, accountantId, file, auth, maxPreviewRows = 0, options = {}) => {
    const { documentDateFrom = null, documentDateTo = null, skipDuplicates = true } = options;

    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', file);
    form.append('MaxPreviewRows', String(maxPreviewRows));
    // Sent with the same value the import will use, so the preview's totals are the import's.
    form.append('SkipDuplicates', String(skipDuplicates));
    // A uniform file holds the whole year. The range scopes what is read, so the preview's totals,
    // missing sort codes and account cards all describe the period that will actually be imported.
    if (documentDateFrom) form.append('DocumentDateFrom', documentDateFrom);
    if (documentDateTo) form.append('DocumentDateTo', documentDateTo);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-bmkvd-journal`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'BMKVD journal preview failed');
    return await res.json();
};

/**
 * Import journal entries from a uniform file, creating any missing account cards.
 * @param {string} caseId - Client case ID
 * @param {File} file - BKMVDATA.txt or a ZIP containing it
 * @param {string} auth - Authorization token
 * @param {Object} options - { defaultSortCodeId, skipDuplicates, autoCreateAccountCards }
 * @returns {Promise<Object>} - { processedCount, errorCount, errorMessages }
 */
const ImportBmkvdJournal = async (caseId, file, auth, options = {}) => {
    const {
        defaultSortCodeId = null,
        skipDuplicates = true,
        autoCreateAccountCards = true,
        sortCodeAssignments = null,
        documentDateFrom = null,
        documentDateTo = null,
        importReconciliations = false
    } = options;

    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('File', file);
    // Only entries the case already holds are skipped; repeats inside the file are always written.
    form.append('SkipDuplicates', String(skipDuplicates));
    form.append('AutoCreateAccountCards', String(autoCreateAccountCards));
    // Also recreate the reconciliations the file records in field 1371 as card matches.
    form.append('ImportReconciliations', String(importReconciliations));
    if (defaultSortCodeId) form.append('DefaultSortCodeId', defaultSortCodeId);
    // The same range the preview ran with, so what was reviewed is what gets imported.
    if (documentDateFrom) form.append('DocumentDateFrom', documentDateFrom);
    if (documentDateTo) form.append('DocumentDateTo', documentDateTo);
    if (sortCodeAssignments && Object.keys(sortCodeAssignments).length > 0) {
        form.append('SortCodeAssignmentsJson', JSON.stringify(sortCodeAssignments));
    }

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-bmkvd-journal`,
        { method: 'POST', headers: { Authorization: `Bearer ${auth}` }, body: form }
    );

    if (!res.ok) throw new Error(await res.text() || 'BMKVD journal import failed');
    return await res.json();
};

/**
 * Preview BMKVD file import - shows what will be imported without creating records
 * @param {string} caseId - Client case ID
 * @param {File} bmkvdFile - BMKVD file (BKMVDATA.txt or similar)
 * @param {string} auth - Authorization token
 * @returns {Promise<Object>} Preview data with parsed documents and validation
 */
const PreviewBmkvdImport = async (caseId, bmkvdFile, auth) => {
    try {
        const formData = new FormData();
        formData.append('caseId', caseId);
        formData.append('file', bmkvdFile);

        const response = await fetch(`${API_URL}/Bmkvd/PreviewImport`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`
            },
            body: formData
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (err) {
        console.error('Error previewing BMKVD import:', err);
        throw err;
    }
};

/**
 * Import BMKVD file and create JournalEntry records
 * @param {string} caseId - Client case ID
 * @param {File} bmkvdFile - BMKVD file (BKMVDATA.txt or similar)
 * @param {string} auth - Authorization token
 * @returns {Promise<Object>} Import result with counts and errors
 */
const ImportBmkvdFile = async (caseId, bmkvdFile, auth) => {
    try {
        const formData = new FormData();
        formData.append('caseId', caseId);
        formData.append('file', bmkvdFile);

        const response = await fetch(`${API_URL}/Bmkvd/Import`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`
            },
            body: formData
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (err) {
        console.error('Error importing BMKVD file:', err);
        throw err;
    }
};

/**
 * Preview HypDoc Income import (.doc/.txt)
 * @param {string} caseId - Case ID
 * @param {string} accountantId - Accountant ID
 * @param {File} file - HypDoc Income file
 * @param {string} [defaultSortCodeId='100'] - Default sort code for income
 * @param {string} auth - Bearer token
 * @returns {Promise<Object>} Preview with entries and missingAccountCards
 */
const PreviewHypDocIncome = async (caseId, accountantId, file, defaultSortCodeId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-hypdoc-income`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview HypDoc Income');
    }
    return response.json();
};

/**
 * Import HypDoc Income file
 */
const ImportHypDocIncome = async (caseId, file, defaultSortCodeId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-hypdoc-income`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import HypDoc Income');
    }
    return response.json();
};

/**
 * Preview iCount Movein.DAT import
 */
const PreviewIcountMoveinDat = async (caseId, accountantId, file, defaultSortCodeId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-icount-moveindat`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview iCount Movein.DAT');
    }
    return response.json();
};

/**
 * Import iCount Movein.DAT file
 */
const ImportIcountMoveinDat = async (caseId, file, defaultSortCodeId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-icount-moveindat`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import iCount Movein.DAT');
    }
    return response.json();
};

const PreviewMorningIncomes = async (caseId, accountantId, file, defaultSortCodeId, defaultDebitAccountId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    if (defaultDebitAccountId) formData.append('defaultDebitAccountId', defaultDebitAccountId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-morning-incomes`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview Morning incomes');
    }
    return response.json();
};

const PreviewMorningPayments = async (caseId, accountantId, file, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-morning-payments`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview Morning payments');
    }
    return response.json();
};

const ImportMorningPayments = async (caseId, file, paymentTypeToCardId, sortCodeId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    formData.append('paymentTypeToCardIdJson', JSON.stringify(paymentTypeToCardId || {}));
    if (sortCodeId != null && sortCodeId !== '') formData.append('sortCodeId', sortCodeId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-morning-payments`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import Morning payments');
    }
    return response.json();
};

const PreviewMavenDocuments = async (caseId, accountantId, file, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-maven-documents`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview Maven documents');
    }
    return response.json();
};

const PreviewXtramJournal = async (caseId, accountantId, file, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-xtram-journal`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview Xtram journal');
    }
    return response.json();
};

const ImportXtramJournal = async (caseId, file, auth, skipDuplicates = true) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    formData.append('SkipDuplicates', String(skipDuplicates ?? true));
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-xtram-journal`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import Xtram journal');
    }
    return response.json();
};

/** Downloads the Xtram journal template (Hebrew columns, RTL) from the API. */
const DownloadXtramJournalTemplate = async (auth) => {
    const response = await fetch(`${API_URL}/ImportExportTransactions/xtram-journal-template`, {
        method: 'GET',
        headers: { "Authorization": `Bearer ${auth}` },
    });
    if (!response.ok) throw new Error('Failed to download Xtram journal template');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'XtramJournalTemplate.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const PreviewWebenefitJournal = async (caseId, accountantId, file, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', file);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-webenefit-journal`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Webenefit journal preview failed');
    return await res.json();
};

const ImportWebenefitJournal = async (caseId, accountantId, file, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', file);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-webenefit-journal`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Webenefit journal import failed');
    return await res.json();
};

const ImportMavenDocuments = async (caseId, accountantId, file, defaultSortCodeId, defaultDebitAccountId, defaultPaymentSortCodeId, paymentTypeToCardId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    if (defaultDebitAccountId) formData.append('defaultDebitAccountId', defaultDebitAccountId);
    if (defaultPaymentSortCodeId) formData.append('defaultPaymentSortCodeId', defaultPaymentSortCodeId);
    formData.append('paymentTypeToCardIdJson', JSON.stringify(paymentTypeToCardId || {}));
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-maven-documents`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import Maven documents');
    }
    return response.json();
};

const ImportMorningIncomes = async (caseId, file, defaultSortCodeId, defaultDebitAccountId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    if (defaultDebitAccountId) formData.append('defaultDebitAccountId', defaultDebitAccountId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-morning-incomes`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import Morning incomes');
    }
    return response.json();
};

const PreviewICountClients = async (caseId, accountantId, file, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-icount-clients`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview iCount clients');
    }
    return response.json();
};

const ImportICountClients = async (caseId, file, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-icount-clients`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import iCount clients');
    }
    return response.json();
};

const PreviewICountInvoices = async (caseId, accountantId, file, defaultSortCodeId, defaultDebitAccountId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    if (defaultDebitAccountId) formData.append('defaultDebitAccountId', defaultDebitAccountId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-icount-invoices`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview iCount invoices');
    }
    return response.json();
};

const ImportICountInvoices = async (caseId, file, defaultSortCodeId, defaultDebitAccountId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    if (defaultDebitAccountId) formData.append('defaultDebitAccountId', defaultDebitAccountId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-icount-invoices`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import iCount invoices');
    }
    return response.json();
};

const PreviewICountInvrec = async (caseId, accountantId, file, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-icount-invrec`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview iCount invrec');
    }
    return response.json();
};

const ImportICountInvrec = async (caseId, file, defaultSortCodeId, defaultDebitAccountId, defaultPaymentSortCodeId, paymentTypeToCardId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    if (defaultDebitAccountId) formData.append('defaultDebitAccountId', defaultDebitAccountId);
    if (defaultPaymentSortCodeId) formData.append('defaultPaymentSortCodeId', defaultPaymentSortCodeId);
    formData.append('paymentTypeToCardIdJson', JSON.stringify(paymentTypeToCardId || {}));
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-icount-invrec`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import iCount invrec');
    }
    return response.json();
};

const PreviewICountRefunds = async (caseId, accountantId, file, defaultSortCodeId, defaultDebitAccountId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    if (defaultDebitAccountId) formData.append('defaultDebitAccountId', defaultDebitAccountId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-icount-refunds`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview iCount refunds');
    }
    return response.json();
};

const ImportICountRefunds = async (caseId, file, defaultSortCodeId, defaultDebitAccountId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    if (defaultDebitAccountId) formData.append('defaultDebitAccountId', defaultDebitAccountId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-icount-refunds`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import iCount refunds');
    }
    return response.json();
};

const PreviewEasyCountClients = async (caseId, accountantId, file, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-easycount-clients`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview EasyCount clients');
    }
    return response.json();
};

const ImportEasyCountClients = async (caseId, file, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-easycount-clients`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import EasyCount clients');
    }
    return response.json();
};

const PreviewEasyCountReport = async (caseId, accountantId, file, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-easycount-report`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview EasyCount report');
    }
    return response.json();
};

const ImportEasyCountReport = async (caseId, file, defaultSortCodeId, defaultDebitAccountId, defaultPaymentSortCodeId, paymentTypeToCardId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    if (defaultSortCodeId) formData.append('defaultSortCodeId', defaultSortCodeId);
    if (defaultDebitAccountId) formData.append('defaultDebitAccountId', defaultDebitAccountId);
    if (defaultPaymentSortCodeId) formData.append('defaultPaymentSortCodeId', defaultPaymentSortCodeId);
    formData.append('paymentTypeToCardIdJson', JSON.stringify(paymentTypeToCardId || {}));
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-easycount-report`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import EasyCount report');
    }
    return response.json();
};

const PreviewVerifone = async (caseId, accountantId, file, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('accountantId', accountantId);
    formData.append('file', file);
    const response = await fetch(`${API_URL}/ImportExportTransactions/preview-verifone`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview Verifone file');
    }
    return response.json();
};

const ImportVerifone = async (caseId, file, sortCodeId, creditAccountId, debitAccountId, auth) => {
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('file', file);
    if (sortCodeId) formData.append('sortCodeId', sortCodeId);
    formData.append('creditAccountId', creditAccountId);
    formData.append('debitAccountId', debitAccountId);
    const response = await fetch(`${API_URL}/ImportExportTransactions/import-verifone`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${auth}` },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import Verifone file');
    }
    return response.json();
};

const PreviewRivhitClosingBalance = async (caseId, accountantId, trialBalanceFile, palCodeFile, year, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', trialBalanceFile);
    form.append('PalCodeFile', palCodeFile);
    form.append('Year', year.toString());

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-rivhit-closing-balance`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Rivhit closing balance preview failed');
    return await res.json();
};

const ImportRivhitClosingBalance = async (caseId, accountantId, trialBalanceFile, palCodeFile, year, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', trialBalanceFile);
    form.append('PalCodeFile', palCodeFile);
    form.append('Year', year.toString());

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-rivhit-closing-balance`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Rivhit closing balance import failed');
    return await res.json();
};

// -----------------------------------------------------------------------------
// Rivhit backup (zip) imports
// -----------------------------------------------------------------------------
export const RivhitBackupImportKind = {
    AccountCards: 'accountCards',
    ClosingBalance: 'closingBalance',
    Movements: 'movements',
};

const RIVHIT_BACKUP_ENDPOINTS = {
    [RivhitBackupImportKind.AccountCards]: { preview: 'AccountCards/Preview', commit: 'AccountCards/Import' },
    [RivhitBackupImportKind.ClosingBalance]: { preview: 'ClosingBalance/Preview', commit: 'ClosingBalance/Import' },
    [RivhitBackupImportKind.Movements]: { preview: 'Movements/Preview', commit: 'Movements/Commit' },
};

const postRivhitBackup = async (path, { caseId, accountantId, file, password, database, year, skipDuplicates, simulate, sortCodes }, auth) => {
    const form = new FormData();
    form.append('File', file);
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    if (password) form.append('Password', password);
    if (database) form.append('Database', database);
    if (year != null) form.append('Year', String(year));
    if (skipDuplicates != null) form.append('SkipDuplicates', String(skipDuplicates));
    if (simulate) form.append('Simulate', simulate);
    if (sortCodes?.length) form.append('SortCodes', JSON.stringify(sortCodes));

    const res = await fetch(`${API_URL}/ImportExportTransactions/ImportRivhit/Backup/${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });

    if (!res.ok) throw Object.assign(new Error(await res.text()), { status: res.status });
    return await res.json();
};

/**
 * Reads a Rivhit backup zip. Errors are thrown with the server's key (e.g. RIVHIT_WRONG_PASSWORD) as the message.
 * @returns {Promise<Object[]>} – the databases of the zip:
 *   [{ path, format, tableCount, tableNames, imports: [{ kind, tables, rowCount, years: [{ year, rowCount }], year }], error }]
 */
const RivhitBackupSuggestions = (request, auth) => postRivhitBackup('Suggestions', request, auth);

/**
 * The preview of one import of a Rivhit backup, the same response as its file import:
 * accountCards – the Rivhit CSV V2 preview; closingBalance – the closing balance preview;
 * movements – { year, ledgerRows, vouchers, oneSidedEntries, vatCards: [{ cardId, cardName }], preview } with the PKUDA preview.
 * @param {string} kind – one of RivhitBackupImportKind
 * @param {{caseId: string, accountantId: string, file: File, password?: string, database?: string, year?: number}} request
 */
const PreviewRivhitBackup = (kind, request, auth) =>
    postRivhitBackup(RIVHIT_BACKUP_ENDPOINTS[kind].preview, request, auth);

/**
 * Imports one import of a Rivhit backup into the client.
 * @param {{caseId: string, accountantId: string, file: File, password?: string, database?: string, year?: number, skipDuplicates?: boolean}} request
 * @returns {Promise<Object>} – { processedCount, errorCount, errorMessages }
 */
const ImportRivhitBackup = (kind, request, auth) =>
    postRivhitBackup(RIVHIT_BACKUP_ENDPOINTS[kind].commit, request, auth);

/**
 * The backup's books of the year against the client's – when they differ and `simulate` names an import
 * ('closingBalance' | 'movements'), against the client's books with what that import would add, nothing saved.
 * @param {{caseId: string, accountantId: string, file: File, password?: string, database?: string, year: number,
 *   simulate?: string, sortCodes?: {sortCodeId: string, sortCodeName?: string, sortCodeGroup?: string,
 *   sortCodeVatType?: number, vat?: number, participatesInProfitAndLoss?: boolean}[]}} request – sortCodes: the
 *   settings chosen for the sort codes the simulated import adds
 * @returns {Promise<Object>} – { year, accountManagement, isDoubleEntry, primary: 'ledger'|'profitAndLoss', books,
 *   tolerance, simulated, importedEntryCount, isSame,
 *   ledger: { lines, sameCount, differentCount, missingCardCount, backupProfit, systemProfit, isSame },
 *   profitAndLoss: { basis, lines, backupProfit, systemProfit, difference, sameCount, differentCount,
 *   missingSortCodeCount, isSame } }
 */
const CompareRivhitBackup = (request, auth) => postRivhitBackup('Compare', request, auth);

/**
 * Reads a Rivhit backup zip for the accountant: the databases with the business of each matched to a client.
 * @returns {Promise<Object[]>} – [{ path, format, error, imports, company: { number, name, caseId, clientName,
 *   accountManagement, matchType, candidates: [{ caseId, name }] } }]
 */
const IdentifyRivhitBackup = async ({ accountantId, file, password }, auth) => {
    const form = new FormData();
    form.append('File', file);
    form.append('AccountantId', accountantId);
    if (password) form.append('Password', password);

    const res = await fetch(`${API_URL}/ImportExportTransactions/ImportRivhit/Backup/Identify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });

    if (!res.ok) throw Object.assign(new Error(await res.text()), { status: res.status });
    return await res.json();
};

// -----------------------------------------------------------------------------
// Ramplus imports
// -----------------------------------------------------------------------------

const PreviewRamplusAccountCards = async (caseId, accountantId, cardsFile, accountsFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', cardsFile);
    if (accountsFile) form.append('AccountsFile', accountsFile);

    const res = await fetch(`${API_URL}/ImportExportTransactions/preview-ramplus-account-cards`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });
    if (!res.ok) throw new Error(await res.text() || 'Ramplus account cards preview failed');
    return await res.json();
};

const ImportRamplusAccountCards = async (caseId, accountantId, cardsFile, accountsFile, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', cardsFile);
    if (accountsFile) form.append('AccountsFile', accountsFile);

    const res = await fetch(`${API_URL}/ImportExportTransactions/import-ramplus-account-cards`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });
    if (!res.ok) throw new Error(await res.text() || 'Ramplus account cards import failed');
    return await res.json();
};

const appendRamplusVatReportOptions = (form, options = {}) => {
    if (options.registrationMonthSource) {
        form.append('RegistrationMonthSource', options.registrationMonthSource);
    }

    if (Array.isArray(options.vatReportFiles) && options.vatReportFiles.length > 0) {
        options.vatReportFiles.forEach((item) => {
            const file = item?.file ?? item;
            const yearMonth = item?.yearMonth;
            if (file) {
                form.append('VatReportFiles', file);
                form.append('VatReportYearMonths', yearMonth || '');
            }
        });
    }
};

const PreviewRamplusJournal = async (caseId, accountantId, journalFile, sortCodeFile, auth, cardsFile = null, options = {}) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('JournalFile', journalFile);
    form.append('SortCodeFile', sortCodeFile);
    if (cardsFile) form.append('CardsFile', cardsFile);
    appendRamplusVatReportOptions(form, options);

    const res = await fetch(`${API_URL}/ImportExportTransactions/preview-ramplus-journal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });
    if (!res.ok) throw new Error(await res.text() || 'Ramplus journal preview failed');
    return await res.json();
};

const ImportRamplusJournal = async (caseId, accountantId, journalFile, sortCodeFile, skipDuplicates, auth, cardsFile = null, options = {}) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('JournalFile', journalFile);
    form.append('SortCodeFile', sortCodeFile);
    form.append('SkipDuplicates', String(skipDuplicates ?? true));
    if (cardsFile) form.append('CardsFile', cardsFile);
    appendRamplusVatReportOptions(form, options);

    const res = await fetch(`${API_URL}/ImportExportTransactions/import-ramplus-journal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });
    if (!res.ok) throw new Error(await res.text() || 'Ramplus journal import failed');
    return await res.json();
};

const PreviewRamplusClosingBalance = async (caseId, accountantId, balanceFile, year, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', balanceFile);
    form.append('Year', year.toString());

    const res = await fetch(`${API_URL}/ImportExportTransactions/preview-ramplus-closing-balance`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });
    if (!res.ok) throw new Error(await res.text() || 'Ramplus closing balance preview failed');
    return await res.json();
};

const ImportRamplusClosingBalance = async (caseId, accountantId, balanceFile, year, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', balanceFile);
    form.append('Year', year.toString());

    const res = await fetch(`${API_URL}/ImportExportTransactions/import-ramplus-closing-balance`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth}` },
        body: form
    });
    if (!res.ok) throw new Error(await res.text() || 'Ramplus closing balance import failed');
    return await res.json();
};

const PreviewSumitClosingBalance = async (caseId, accountantId, balanceFile, year, auth, profitLossFile = null) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', balanceFile);
    form.append('Year', year.toString());
    if (profitLossFile) form.append('ProfitLossFile', profitLossFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-sumit-closing-balance`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Sumit closing balance preview failed');
    return await res.json();
};

const ImportSumitClosingBalance = async (caseId, accountantId, balanceFile, year, auth, profitLossFile = null) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', balanceFile);
    form.append('Year', year.toString());
    if (profitLossFile) form.append('ProfitLossFile', profitLossFile);

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-sumit-closing-balance`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Sumit closing balance import failed');
    return await res.json();
};

const PreviewFinbotClosingBalance = async (caseId, accountantId, balanceFile, year, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', balanceFile);
    form.append('Year', year.toString());

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/preview-finbot-closing-balance`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot closing balance preview failed');
    return await res.json();
};

const ImportFinbotClosingBalance = async (caseId, accountantId, balanceFile, year, auth) => {
    const form = new FormData();
    form.append('CaseId', caseId);
    form.append('AccountantId', accountantId);
    form.append('File', balanceFile);
    form.append('Year', year.toString());

    const res = await fetch(
        `${API_URL}/ImportExportTransactions/import-finbot-closing-balance`,
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth}` },
            body: form
        }
    );

    if (!res.ok) throw new Error(await res.text() || 'Finbot closing balance import failed');
    return await res.json();
};

const ImportExportService = {
    ExportTransactionReportMovinDat,
    PreviewExportTransactionReportMovinDat,
    PreviewMovinDat,
    PreviewExportTransactionReportPkuda,
    ExportTransactionReportPkuda,
    PreviewExportAccountCards,
    ExportAccountCardsPkuda,
    PreviewMdbJournalEntries,
    PreviewExcelJournalEntries,
    ImportTransactionReportMovinDat,
    PreviewHeshinAccountCards,
    ImportHeshinAccountCards,
    ImportAccountCardsHeshinDat,
    ExportAccountCardsHeshinDat,
    ImportProblematicAccountCards,
    PreviewTransactionReportPkuda,
    CommitTransactionReportPkuda,
    PreviewTransactionReportAtzmaitCsv,
    CommitTransactionReportAtzmaitCsv,
    PreviewAtzmaitBackup,
    CommitAtzmaitBackup,
    UploadAccountCardsFileDataRevhit,
    ImportMdbAccountCards,
    ImportMdbJournalEntries,
    ImportExcelJournalEntries,
    PreviewExcelAccountCards,
    ImportExcelAccountCards,
    // Sumit Import Functions
    PreviewSumitAccountCards,
    ImportSumitAccountCards,
    PreviewSumitJournal,
    ImportSumitJournal,
    // Finbot Import Functions
    PreviewFinbotAccountCards,
    ImportFinbotAccountCards,
    PreviewFinbotJournal,
    ImportFinbotJournal,
    // Finbot API Import Functions
    GetFinbotClients,
    PreviewFinbotJournalApi,
    ImportFinbotJournalApi,
    PreviewFinbotAccountCardsApi,
    ImportFinbotAccountCardsApi,
    // Paperless API Import Functions
    GetPaperlessUsers,
    PreviewPaperlessApi,
    ImportPaperlessApi,
    // Ravginit (RAVGONIT .26) Import Functions
    PreviewRavginitAccountCards,
    ImportRavginitAccountCards,
    PreviewRavginitJournal,
    ImportRavginitJournal,
    // KodBena Import Functions
    PreviewKodBenaAccountCards,
    ImportKodBenaAccountCards,
    PreviewKodBenaJournal,
    ImportKodBenaJournal,
    // Taiodet Import Functions
    PreviewTaiodetAccountCards,
    ImportTaiodetAccountCards,
    PreviewTaiodetJournal,
    ImportTaiodetJournal,
    PreviewPriorityAccountCards,
    ImportPriorityAccountCards,
    PreviewPriorityJournal,
    ImportPriorityJournal,
    PreviewPriorityOpeningBalance,
    ImportPriorityOpeningBalance,
    // Rivhit CSV V2 Import Functions
    PreviewRivhitCsvV2,
    ImportRivhitCsvV2,
    // Account Index Import Functions (RAVGONIT / NIHULIT)
    PreviewRavginitAccountIndex,
    ImportRavginitAccountIndex,
    PreviewNihulitAccountIndex,
    ImportNihulitAccountIndex,
    // BMKVD (Uniform File) Functions
    PreviewBmkvdExport,
    DownloadBmkvdExport,
    PreviewBmkvdImport,
    ImportBmkvdFile,
    PreviewBmkvdJournal,
    ImportBmkvdJournal,
    // HypDoc Income
    PreviewHypDocIncome,
    ImportHypDocIncome,
    // iCount Movein.DAT
    PreviewIcountMoveinDat,
    ImportIcountMoveinDat,
    // Morning Incomes
    PreviewMorningIncomes,
    ImportMorningIncomes,
    // Morning Payments
    PreviewMorningPayments,
    ImportMorningPayments,
    // Maven
    PreviewMavenDocuments,
    ImportMavenDocuments,
    // iCount CSV
    PreviewICountClients,
    ImportICountClients,
    PreviewICountInvoices,
    ImportICountInvoices,
    PreviewICountInvrec,
    ImportICountInvrec,
    PreviewICountRefunds,
    ImportICountRefunds,
    // EasyCount CSV
    PreviewEasyCountClients,
    ImportEasyCountClients,
    PreviewEasyCountReport,
    ImportEasyCountReport,
    // Verifone Credit Terminal
    PreviewVerifone,
    ImportVerifone,
    // Xtram Journal
    PreviewXtramJournal,
    ImportXtramJournal,
    DownloadXtramJournalTemplate,
    // Webenefit Journal
    PreviewWebenefitJournal,
    ImportWebenefitJournal,
    // Rivhit Closing Balance
    PreviewRivhitClosingBalance,
    ImportRivhitClosingBalance,
    // Rivhit backup (zip)
    RivhitBackupSuggestions,
    PreviewRivhitBackup,
    ImportRivhitBackup,
    CompareRivhitBackup,
    IdentifyRivhitBackup,
    // Ramplus
    PreviewRamplusAccountCards,
    ImportRamplusAccountCards,
    PreviewRamplusJournal,
    ImportRamplusJournal,
    PreviewRamplusClosingBalance,
    ImportRamplusClosingBalance,
    // Sumit Closing Balance
    PreviewSumitClosingBalance,
    ImportSumitClosingBalance,
    // Finbot Closing Balance
    PreviewFinbotClosingBalance,
    ImportFinbotClosingBalance,
};

export default ImportExportService;
